import { Button, Icon, Stack, Text } from "@assetwatch/ui";
import { useId } from "react";
import type { SelectableRequestPriority } from "../../../types/workRequest";
import { RequestFieldLabel } from "./RequestFieldLabel";

type PrioritySelectorProps = {
  /** Null while the requester has not chosen a priority. */
  value: SelectableRequestPriority | null;
  onChange: (value: SelectableRequestPriority | null) => void;
  /** Assessment suggestion; shown as a hint, never applied automatically. */
  suggestedValue?: SelectableRequestPriority | null;
};

export const PRIORITY_LABELS: Record<SelectableRequestPriority, string> = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
};

const PRIORITY_OPTIONS: SelectableRequestPriority[] = ["LOW", "MEDIUM", "HIGH"];

/** The assessment's suggested priority, shown as a hint only. */
export function PrioritySuggestion({
  value,
}: {
  value: SelectableRequestPriority;
}) {
  return (
    <span className="flex items-center gap-(--spacing-4) text-text-subtle">
      <Icon name="sparkles" size="xs" tone="inherit" />
      <Text as="span" size="sm" tone="subtle">
        Suggested: {PRIORITY_LABELS[value]}
      </Text>
    </span>
  );
}

export function PrioritySelector({
  value,
  onChange,
  suggestedValue = null,
}: PrioritySelectorProps) {
  const labelId = useId();

  return (
    <Stack gap={4}>
      <RequestFieldLabel id={labelId}>Priority</RequestFieldLabel>
      <fieldset
        aria-labelledby={labelId}
        className="m-0 grid grid-cols-3 gap-(--spacing-12) border-0 p-0"
      >
        {PRIORITY_OPTIONS.map((priority) => {
          const isSelected = value === priority;
          return (
            <Button
              aria-pressed={isSelected}
              fullWidth
              key={priority}
              // Selecting the active option again clears it: priority is optional.
              onClick={() => onChange(isSelected ? null : priority)}
              variant={isSelected ? "primary" : "outline"}
            >
              {PRIORITY_LABELS[priority]}
            </Button>
          );
        })}
      </fieldset>
      {suggestedValue ? <PrioritySuggestion value={suggestedValue} /> : null}
    </Stack>
  );
}
